/**
 * Kalender-Abonnement-Dialog für /einsatzplan.
 * 
 * Zeigt dem Mitarbeiter den persönlichen iCal-Subscription-Link mit
 * Copy-Button + Anleitung für Outlook / Apple / Google Kalender.
 * Token kann regeneriert werden — alte Subscriptions werden ungültig.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { Copy, Check, RefreshCw, Calendar, Apple, Mail, ExternalLink, Info } from 'lucide-react';
import { icalAPI } from '../lib/api';
import { toast } from 'sonner';

export function CalendarSubscribeDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [tokenPath, setTokenPath] = useState('');
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const fullUrl = tokenPath
    ? `${window.location.origin}${tokenPath}`
    : '';
  // Manche Apps wollen webcal:// statt https://
  const webcalUrl = fullUrl ? fullUrl.replace(/^https?:/, 'webcal:') : '';

  const loadToken = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await icalAPI.getMyToken();
      setTokenPath(data.path);
    } catch (err) {
      console.error('Token-Abruf fehlgeschlagen', err);
      toast.error('Kalender-Token konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !tokenPath) {
      loadToken();
    }
  }, [open, tokenPath, loadToken]);

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Link kopiert');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Konnte nicht kopieren');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm(
      'Wirklich neuen Token erzeugen?\n\nAlle bestehenden Kalender-Abonnements werden sofort ungültig und müssen mit dem neuen Link erneut eingerichtet werden.'
    )) return;
    setRegenerating(true);
    try {
      const { data } = await icalAPI.regenerateMyToken();
      setTokenPath(data.path);
      toast.success('Neuer Token erzeugt — alte Abonnements sind jetzt ungültig.');
    } catch (err) {
      console.error('Regenerate fehlgeschlagen', err);
      toast.error('Token konnte nicht erneuert werden.');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ical-subscribe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Einsätze im Kalender abonnieren
          </DialogTitle>
          <DialogDescription>
            Füge deine Einsätze zu Outlook, Apple Kalender oder Google Kalender hinzu.
            Der Kalender aktualisiert sich automatisch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Subscription URL */}
          <div className="space-y-2">
            <Label htmlFor="ical-url">Dein persönlicher Kalender-Link</Label>
            {loading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <div className="flex gap-2">
                <Input
                  id="ical-url"
                  value={fullUrl}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="ical-subscribe-url"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(fullUrl)}
                  disabled={!fullUrl}
                  data-testid="ical-copy-button"
                  title="In Zwischenablage kopieren"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              Der Link enthält einen geheimen Token. Teile ihn niemandem — wer den Link hat,
              sieht deine Einsätze.
            </p>
          </div>

          {/* Setup-Anleitungen */}
          <Tabs defaultValue="apple" className="space-y-3">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="apple" data-testid="ical-tab-apple">
                <Apple className="h-4 w-4 mr-2" />
                Apple
              </TabsTrigger>
              <TabsTrigger value="outlook" data-testid="ical-tab-outlook">
                <Mail className="h-4 w-4 mr-2" />
                Outlook
              </TabsTrigger>
              <TabsTrigger value="google" data-testid="ical-tab-google">
                <Calendar className="h-4 w-4 mr-2" />
                Google
              </TabsTrigger>
            </TabsList>

            <TabsContent value="apple" className="space-y-2 text-sm">
              <p className="font-medium">iPhone / iPad / Mac:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground pl-2">
                <li>Öffne die <strong>Kalender</strong>-App</li>
                <li>Wähle <strong>Datei → Neues Kalenderabonnement…</strong> (Mac) bzw. <strong>Einstellungen → Account hinzufügen → Andere → Kalenderabo</strong> (iOS)</li>
                <li>Füge den Link unten ein und bestätige</li>
              </ol>
              {webcalUrl && (
                <Button asChild variant="outline" size="sm" className="mt-2" data-testid="ical-apple-direct">
                  <a href={webcalUrl}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    Direkt in Apple Kalender öffnen
                  </a>
                </Button>
              )}
            </TabsContent>

            <TabsContent value="outlook" className="space-y-2 text-sm">
              <p className="font-medium">Outlook (Web):</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground pl-2">
                <li>Öffne <a href="https://outlook.live.com/calendar/" target="_blank" rel="noreferrer" className="text-primary underline">Outlook Kalender</a></li>
                <li>Klicke auf <strong>Kalender hinzufügen → Aus dem Internet abonnieren</strong></li>
                <li>Füge den Link oben ein, gib einen Namen ein und bestätige</li>
              </ol>
              <p className="font-medium pt-1">Outlook Desktop:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground pl-2">
                <li><strong>Datei → Kontoeinstellungen → Internetkalender → Neu</strong></li>
                <li>Link einfügen und bestätigen</li>
              </ol>
            </TabsContent>

            <TabsContent value="google" className="space-y-2 text-sm">
              <p className="font-medium">Google Kalender:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground pl-2">
                <li>Öffne <a href="https://calendar.google.com/" target="_blank" rel="noreferrer" className="text-primary underline">Google Kalender</a> (nur Web, nicht App)</li>
                <li>Links: <strong>Weitere Kalender → + → Per URL</strong></li>
                <li>Füge den Link oben ein und klicke <strong>Kalender hinzufügen</strong></li>
              </ol>
              <Alert className="mt-2">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Google synchronisiert externe Kalender meist alle 12–24 Stunden — Änderungen
                  erscheinen also nicht sofort.
                </AlertDescription>
              </Alert>
            </TabsContent>
          </Tabs>

          <Alert variant="default" className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-xs">
              Der Kalender enthält deine Einsätze von <strong>30 Tagen rückwirkend</strong> bis
              <strong> 12 Monate in der Zukunft</strong>.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleRegenerate}
            disabled={regenerating || loading}
            data-testid="ical-regenerate-button"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
            Neuen Link erzeugen
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="ical-close-button">
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
